/**
 * `feedRollDice` — project an ad-hoc `/roll` feed entry ({@link RollFeedEntry}) onto the
 * physical dice the overlay throws. This is the bridge that makes a free-form `/roll 2d6+3`
 * (typed from anywhere — the desktop or the combat surface) tumble on the global dice surface,
 * landing on the faces the server already recorded.
 *
 * **The roll authority is the server, not this module.** Chat owns the one authoritative
 * `/roll` command (`backend/src/diceNotation.ts` → `dice.ts`, no second RNG) and records every
 * draw into a durable {@link RollFeedEntry} (`notation`/`groups`/`total`). We only *read* that
 * record — one {@link DiceSpec} per recorded draw, each carrying its recorded face and
 * kept/dropped flag, and taking its die size from **its own group** (a `/roll` is compound, so
 * `1d20 + 2d6` throws one d20 mesh and two d6 meshes in one batch).
 * Dropped draws (`kept[i] === false`, from `kh`/`kl`) surface as a dimmed die, never as a
 * separate source of truth. This is the {@link RollFeedEntry} analogue of
 * {@link ./rollChoreography}'s `diceSpecsFromRoll` (a `RollResult`) and {@link ./resolutionDice}'s
 * `diceSpecsFromSteps` (a `ResolvedStep[]`).
 *
 * Pure + framework-free (no `three`/`cannon-es`/DOM): the only dependency is the `RollFeedEntry`
 * **type** from `@lancer-vtt/shared`. It reads data that already crosses the wire — no reducer or
 * extra protocol round-trip — preserving the dice module's isolation invariant (remove the
 * overlay subscriber and the feed/COMMS.app stay fully functional).
 */
import type { RollFeedEntry } from "@lancer-vtt/shared";
import type { DiceSpec } from "./rollChoreography";

/**
 * The die shapes the overlay actually has geometry for (`geometryForSides` in `dieGeometry.ts`):
 * the three the rules engine rolls (20/6/3) plus the cosmetic d8/d12/d10/d4 — real
 * octahedron/dodecahedron/trapezohedron/tetrahedron meshes, ad-hoc `/roll` display only.
 *
 * Chat's `/roll` parser (`backend/src/diceNotation.ts`) accepts **any** `dM` up to `MAX_SIDES`
 * (`d100`/…). Those unmodelled sizes must never reach `geometryForSides` (it throws by design, to
 * keep the combat path strict), so the feed projection maps them onto a supported mesh here — the
 * one place that's safe, because this path is display-only. **d100 stays on the d20-shell
 * fallback deliberately**, and is the only unmodelled size left.
 */
const SUPPORTED_SIDES = new Set([20, 6, 3, 8, 12, 10, 4]);

/**
 * Choose a **displayable** mesh for a recorded pool `sides`. The rule (feed/ad-hoc path only —
 * the combat path stays strict):
 *
 * - A supported size (20/6/3/8/12/10/4) renders on its own mesh, faithfully.
 * - Any other size (d100/…) falls back to the **d20** mesh — its faces are 1–20, the
 *   widest range, so it covers every smaller still-unmodelled cosmetic die fully and shows any
 *   larger-die roll ≤ 20.
 *
 * A recorded value no supported mesh can print faithfully (e.g. a `d100` roll of 73) still
 * renders on the d20 mesh; the physical die lands best-effort (the panel's `notation = total`
 * readout carries the true result, so it's never lost) and `orientToValue` no-ops on the
 * unprintable target rather than throwing — see {@link diceSpecsFromFeedRoll}.
 */
function displayableSides(sides: number): number {
  return SUPPORTED_SIDES.has(sides) ? sides : 20;
}

/**
 * Project a `/roll` feed entry onto the dice to throw — **every group's** recorded draws
 * flattened in notation order, one die per draw, each carrying its recorded `value` and kept
 * flag (a dropped die renders dimmed) and the mesh of **its own** group's `sides`. A compound
 * `1d20 + 2d6` therefore tumbles a d20 alongside two d6s in the same batch. Each group's mesh
 * `sides` is always a **supported** shape ({@link displayableSides}); an ad-hoc `d8`/`d100` is
 * shown on the d20 mesh rather than crashing the overlay.
 *
 * A **subtracted** group (`sign === -1`) still throws its dice — the physical faces are what
 * was rolled, and the readout panel carries the signed total; there's no "negative die" to show.
 *
 * This is the whole "what to show" contract: the overlay lands each die on its recorded face
 * when that value is printable, else best-effort (the true total is on the readout panel).
 */
export function diceSpecsFromFeedRoll(entry: RollFeedEntry): DiceSpec[] {
  return entry.groups.flatMap((group) => {
    const sides = displayableSides(group.sides);
    return group.draws.map((value, i) => ({
      sides,
      value,
      kept: group.kept[i] ?? true,
    }));
  });
}

/**
 * A terse, display-only readout for the overlay panel — the recorded notation and total the
 * server stamped, never recomputed (e.g. `2d6kh1+3 = 14`). The notation is already normalized
 * server-side, so this is a straight echo of the authoritative record.
 */
export function formatFeedRollSummary(entry: RollFeedEntry): string {
  return `${entry.notation} = ${entry.total}`;
}
